import React, { useEffect, useState } from 'react';
import Dashboard from './components/dashboard/Dashboard';
import Button from './components/common/Button';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMoon, HiSun } from 'react-icons/hi';
import './index.css';
import './App.css';

type Theme = 'light' | 'dark' | 'blue' | 'purple';

function App() {
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as Theme) || 'light';
  });

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });

  useEffect(() => {
    // Apply theme
    const root = document.documentElement;
    
    // Toggle dark mode
    if (isDark || currentTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Apply theme-specific styles
    switch (currentTheme) {
      case 'blue':
        root.style.setProperty('--color-primary', '#3b82f6');
        break;
      case 'purple':
        root.style.setProperty('--color-primary', '#8b5cf6');
        break;
      default:
        root.style.setProperty('--color-primary', '#0ea5e9');
    }

    // Save preference
    localStorage.setItem('theme', currentTheme);
  }, [currentTheme, isDark]);

  const handleThemeChange = (theme: Theme) => {
    setCurrentTheme(theme);
    setIsDark(theme === 'dark');
  };

  const toggleDarkMode = () => {
    setIsDark(!isDark);
    if (!isDark) {
      setCurrentTheme('dark');
    } else {
      setCurrentTheme('light');
    }
  };

  return (
    <div className="app">
      {/* Theme Switcher */}
      <div className="theme-switcher">
        {/* Dark Mode Toggle */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleDarkMode}
          className="p-2 rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
          aria-label="Toggle dark mode"
        >
          <AnimatePresence mode="wait" initial={false}>
            {isDark ? (
              <motion.div
                key="sun"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <HiSun className="w-5 h-5" />
              </motion.div>
            ) : (
              <motion.div
                key="moon"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <HiMoon className="w-5 h-5" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        <span className="theme-label">Theme:</span>
        
        <div className="theme-buttons">
          <Button
            size="sm"
            variant={currentTheme === 'light' && !isDark ? 'primary' : 'ghost'}
            onClick={() => handleThemeChange('light')}
          >
            Light
          </Button>
          <Button
            size="sm"
            variant={currentTheme === 'blue' ? 'primary' : 'ghost'}
            onClick={() => handleThemeChange('blue')}
          >
            Blue
          </Button>
          <Button
            size="sm"
            variant={currentTheme === 'purple' ? 'primary' : 'ghost'}
            onClick={() => handleThemeChange('purple')}
          >
            Purple
          </Button>
        </div>
      </div>

      {/* Main Dashboard */}
      <Dashboard />

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#f1f5f9' : '#0f172a',
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            borderRadius: '12px',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '500',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
